// @flow
/* global window */

import * as React from 'react';

import isEmpty from 'lodash/isEmpty';
import { toUnicode } from 'punycode.js';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { withPageMeta } from '@gandi/app-runner';
import { needsAuth } from '@gandi/authentication';
import {
  getClaims,
  updateProduct,
  selectClaimsData,
  selectOneProductById,
  type Product,
  type Claims as ClaimsType,
} from '@gandi/cart';
import type { Contact } from '@gandi/domain-configuration';
import { withTranslator, type TranslatorProps } from '@gandi/translate';
import { Alert, Base, Button } from '@gandi/ui';
import { Card } from '@gandi/ui/deprecated';

import { Intro } from 'src/Layout';
import { Layout } from 'src/components/Layout';
import { withRouter } from 'src/helpers/withRouter';
import type {
  PreFetchArgs,
  PreFetchReturn,
  Dispatch,
  WithRouterProps,
  FullState,
} from 'src/types';

import ClaimsForm from './components';

import styles from './Claims.scss';

const alertMessage = (__: TranslatorProps['__']) => (
  <div>
    <p className={styles.justify}>
      {__(
        'In accordance with applicable rules, we must inform you of this fact. The owner of ' +
          'the trademark will also receive a notification and may, in the event that they consider ' +
          'the registration to be abusive, exercise their rights to the domain name.',
      )}
    </p>
    <p className={styles.justify}>
      {__(
        'If you would nevertheless like to register the domain name(s), validate the ' +
          'Trademark Notice by reading the trademark information and then checking the ' +
          'acknowlegement notice at the bottom of this page before submitting.',
      )}
    </p>
  </div>
);

const introMessage = (__: TranslatorProps['__']) => (
  <div>
    <p className={styles.justify}>
      {__(
        'You have received this Trademark Notice because you have applied for a domain ' +
          'name which matches at least one trademark record submitted to the Trademark ' +
          'Clearinghouse.',
      )}
    </p>
    <p className={styles.justify}>
      {__(
        'You may or may not be entitled to register the domain name depending on your ' +
          'intended use and whether it is the same or significantly overlaps with the trademarks ' +
          'listed below. Your rights to register this domain name may or may not be protected as ' +
          'noncommercial use or “fair use” by the laws of your country.',
      )}
    </p>
    <p className={styles.justify}>
      {__(
        'Please read the trademark information below carefully, including the trademarks, ' +
          'jurisdictions, and goods and services for which the trademarks are registered. Please ' +
          'be aware that not all jurisdictions review trademark applications closely, so some of ' +
          'the trademark information below may exist in a national or regional registry which ' +
          'does not conduct a thorough or substantive review of trademark rights prior to ' +
          'registration. If you have questions, you may want to consult an attorney or legal ' +
          'expert on trademarks and intellectual property for guidance.',
      )}
    </p>
    <p className={styles.justify}>
      {__(
        'If you continue with this registration, you represent that, you have received ' +
          'and you understand this notice and to the best of your knowledge, your registration ' +
          'and use of the requested domain name will not infringe on the trademark rights listed ' +
          'below. The following mark is listed in the Trademark Clearinghouse:',
      )}
    </p>
  </div>
);

type Params = {
  cartId: string,
  productId: string,
};

type ClaimsConfigureProps = {
  dispatch: Dispatch,
  claimsData: ClaimsType,
  product: ?Product,
  __: TranslatorProps['__'],
  ...WithRouterProps<Params>,
};

class Claims extends React.Component<ClaimsConfigureProps> {
  /*
   * Get the claims data if not already available.
   */
  static preFetch(args: PreFetchArgs<{}, Params>): PreFetchReturn<{}> {
    const {
      dispatch,
      params: { productId },
    } = args;

    return dispatch(getClaims(productId));
  }

  componentDidMount() {
    // Workaround as not implemented in JSDOM for tests
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }

  /**
   * Save the claims validation in product payload
   */
  saveClaims: () => Promise<void> = () => {
    const {
      claimsData,
      dispatch,
      params: { cartId, productId },
      navigate,
    } = this.props;

    if (!this.props.product) {
      return Promise.resolve();
    }

    const payload = {
      ...this.props.product.payload,
      claims: claimsData.notice_id,
    };

    return dispatch(updateProduct({ productId, payload })).then(() =>
      navigate(`/cart/${cartId}/overview`),
    );
  };

  renderContacts: (contact: {
    name?: string,
    org?: string,
    street?: string,
    city?: string,
    pc?: string,
    cc?: string,
    phone?: string,
    fax?: string,
    email?: string,
    ...
  }) => React.MixedElement = contact => {
    const { __ } = this.props;

    return (
      <ul key={contact.name || contact.org}>
        {contact.name && (
          <li>
            <strong>{__('Name:')}</strong> {contact.name}
          </li>
        )}
        {contact.org && (
          <li>
            <strong>{__('Organization:')}</strong> {contact.org}
          </li>
        )}
        {contact.street && (
          <li>
            <strong>{__('Address:')}</strong> {contact.street}
          </li>
        )}
        {contact.city && (
          <li>
            <strong>{__('City:')}</strong> {contact.city}
          </li>
        )}
        {contact.pc && (
          <li>
            <strong>{__('Postal Code:')}</strong> {contact.pc}
          </li>
        )}
        {contact.cc && (
          <li>
            <strong>{__('Country:')}</strong> {contact.cc}
          </li>
        )}
        {contact.phone && (
          <li>
            <strong>{__('Phone:')}</strong> {contact.phone}
          </li>
        )}
        {contact.fax && (
          <li>
            <strong>{__('Fax:')}</strong> {contact.fax}
          </li>
        )}
        {contact.email && (
          <li>
            <strong>{__('Email:')}</strong> {contact.email}
          </li>
        )}
      </ul>
    );
  };

  renderClaims: (claim: {
    mark_name: string,
    jurisdiction_country: string,
    goods_and_services: string,
    holders: Array<string>,
    contacts: Array<Contact>,
    ...
  }) => React.Node = claim => {
    const { __ } = this.props;

    return (
      <Card key={claim.mark_name}>
        <h3>
          {__('Trademark:')} {claim.mark_name}
        </h3>
        <ul className={styles.list}>
          <li className={styles.listItem}>
            <strong>{__('Juridiction:')}</strong> {claim.jurisdiction_country}
          </li>
          <li className={styles.listItem}>
            <strong>{__('Goods and Services:')}</strong>{' '}
            {claim.goods_and_services}
          </li>
          <li className={styles.listItem}>
            <strong>{__('Trademark Registrant:')}</strong>{' '}
            {/* $FlowFixMe[incompatible-call] */}
            {claim.holders.map(this.renderContacts)}
          </li>
          <li className={styles.listItem}>
            <strong>{__('Trademark Agent:')}</strong>{' '}
            {/* $FlowFixMe[prop-missing] */}
            {/* $FlowFixMe[incompatible-call] */}
            {claim.contacts.map(this.renderContacts)}
          </li>
        </ul>
      </Card>
    );
  };

  renderContent(): React.MixedElement | React.Node {
    const { __, claimsData, product } = this.props;

    if (!product) {
      return (
        <Base marginBottom="base">
          <Alert
            intent="danger"
            message={__('The product has not been found!')}
          />
        </Base>
      );
    }

    // $FlowFixMe[incompatible-call]
    const cleanIdentifier = toUnicode(product.fqdn);

    return (
      <div>
        <Intro
          title={__('Trademark notification for %(identifier)s', {
            identifier: cleanIdentifier,
          })}
        />

        {isEmpty(claimsData) && (
          <Base marginBottom="base">
            <Alert
              message={__(
                'This domain name does not contain a registered trademark',
              )}
              intent="info"
            />
          </Base>
        )}

        {!isEmpty(claimsData) && (
          <div>
            <Base marginBottom="base">
              <Alert
                title={__('This domain name contains a registered trademark')}
                message={alertMessage(__)}
                intent="warning"
              />
            </Base>

            {introMessage(__)}

            {claimsData.claims.map(this.renderClaims)}

            <p
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={__(
                'For more information concerning the trademark, please see the ' +
                  '<a href="">online databases</a>.',
                { withHTML: true },
              )}
            />

            {/* $FlowFixMe[prop-missing]: only `__` is used but type needs the whole translator, the easiest way to fix is to use translator hook in child */}
            <ClaimsForm
              __={__}
              initialValues={{ claim: Boolean(product.payload.claims) }}
              onSubmit={this.saveClaims}
            />
          </div>
        )}
      </div>
    );
  }

  /**
   * Render the component
   */
  render(): React.Node {
    const {
      __,
      claimsData,
      params: { cartId },
    } = this.props;

    const noClaims = isEmpty(claimsData);

    return (
      <Layout>
        {this.renderContent()}
        <Base flex flexJustifyBetween flexWrap marginY="base">
          <Button
            type="button"
            to={`/cart/${cartId}/overview`}
            kind="outline"
            className={styles.button}
          >
            {__('Back')}
          </Button>
          <Button
            type="submit"
            // $FlowFixMe FORM_ID is class prop
            form={ClaimsForm.FORM_ID}
            className={styles.button}
            disabled={noClaims}
          >
            {__('Continue')}
          </Button>
        </Base>
      </Layout>
    );
  }
}

const mapStateToProps = (
  state: FullState,
  ownProps: WithRouterProps<Params>,
) => ({
  claimsData: selectClaimsData(state, {
    productId: ownProps.params.productId,
  }),
  product: selectOneProductById(state, {
    productId: ownProps.params.productId,
  }),
});

export default (compose(
  needsAuth,
  withRouter,
  withTranslator(),
  withPageMeta(args => ({
    title: args.__('Claims for the domain name to purchase - Gandi.net'),
  })),
  connect(mapStateToProps),
)(Claims): React.ComponentType<{}>);
